import React, {
  ReactNode,
  type MouseEventHandler,
  useEffect,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  MouseEvent,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import IconButton from '@mui/material/IconButton';
import {
  NPISearchItemDTO,
  ProviderEntryDTO,
} from 'dtos';
import ProviderContactConfigDialog from 'pages/Dashboard/components/ReferringProvider/ProviderContactConfigDialog';
import ProviderCard from 'pages/Dashboard/components/ReferringProvider/ProviderCard';
import {
  getProviderEntryConfigTarget,
} from 'pages/Dashboard/components/ReferringProvider/providerEntryConfigTarget';
import ProviderSearchDialog from 'pages/Dashboard/components/ReferringProvider/ProviderSearchDialog';
import CompactDateEditor from 'pages/Dashboard/components/ReferringProvider/CompactDateEditor';
import EditIcon from 'core/icons/edit';
import ToolTip from 'core/components/Tooltip';
import NPIInfo from 'pages/Dashboard/components/NPIInfo';
import Name from 'pages/Dashboard/components/Name';
import Editable, {
  EditableInstance,
} from 'core/components/Editable';
import {
  Button,
} from 'pages/Dashboard/components/Button';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  type Suggestion as ProviderSuggestion,
} from 'pages/Dashboard/components/ReferringProvider/ProviderSearchDialog';

type Parts = 'root' | 'button' | 'valueContainer' | 'name' | 'npiInfo' | 'date';
type Suggestion = ProviderSuggestion & {
  value?: ProviderEntryDTO;
};
type DateSeenValue = {
  firstSeenOn?: string | null;
  lastSeenOn?: string | null;
};
type DateSeenProps = DateSeenValue & {
  onChange?: (value: DateSeenValue) => void;
};

type Props = {
  onChange: (value?: NPISearchItemDTO | null) => void;
  dialogHeight?: number;
  value?: ProviderEntryDTO | null;
  loading?: boolean;
  classes?: Partial<Record<Parts, string>>;
  hideBackdrop?: boolean;
  text?: string;
  action?: ReactNode;
  suggestions?: Suggestion[];
  variant?: 'button' | 'section' | 'compact' | 'compactBorder';
  dateSeen?: DateSeenProps;
  showContactButton?: boolean;
  disabled?: boolean;
};

type ProviderButtonProps = {
  value?: ProviderEntryDTO | null;
  onOpenDialog: MouseEventHandler<HTMLButtonElement>;
  onClear: MouseEventHandler<HTMLButtonElement>;
  onOpenContact: MouseEventHandler<HTMLButtonElement>;
  classes?: Partial<Record<Parts, string>>;
  text?: string;
  variant?: 'button' | 'section' | 'compact' | 'compactBorder';
  showContactButton: boolean;
  dateSeen?: DateSeenProps;
  dialogOpen?: boolean;
  disabled?: boolean;
};

type ReferringProviderState = {
  mode: number;
  setMode: Dispatch<SetStateAction<number>>;
  open: boolean;
  contactOpen: boolean;
  openDialog: (nextMode: number) => void;
  closeDialog: () => void;
  openContact: () => void;
  closeContact: () => void;
};

function useReferringProviderState() {
  const [mode, setMode] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const [contactOpen, setContactOpen] = useState<boolean>(false);

  return {
    mode,
    setMode,
    open,
    contactOpen,
    openDialog: (nextMode: number) => {
      setMode(nextMode);
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
      setMode(0);
    },
    openContact: () => {
      setContactOpen(true);
    },
    closeContact: () => {
      setContactOpen(false);
    },
  } satisfies ReferringProviderState;
}

type ProviderActionsProps = {
  text: string;
  onEdit: MouseEventHandler<HTMLButtonElement>;
  onClear: MouseEventHandler<HTMLButtonElement>;
  onContact: MouseEventHandler<HTMLButtonElement>;
  showContactButton: boolean;
  showClear?: boolean;
  compact?: boolean;
};

function ProviderActions({
  text,
  onEdit,
  onClear,
  onContact,
  showContactButton,
  showClear = true,
  compact = false,
}: ProviderActionsProps) {
  const iconSizeClassName = compact
    ? '!h-5 !w-5 !text-black'
    : '!w-4 !h-4';
  const providerText = text.toLowerCase();
  const buttonClassName = compact
    ? '!h-8 !w-8 !p-1 !text-gray-500 hover:!bg-gray-100 hover:!text-gray-900'
    : '!p-0';

  return (
    <div className="flex shrink-0 gap-1">
      <ToolTip title={`Change ${providerText}`}>
        <IconButton
          className={buttonClassName}
          aria-label={`Change ${providerText}`}
          onClick={onEdit}
        >
          <EditIcon className={iconSizeClassName} />
        </IconButton>
      </ToolTip>
      {showContactButton && (
        <ToolTip title={`Contact ${providerText}`}>
          <IconButton
            className={buttonClassName}
            aria-label={`Contact ${providerText}`}
            onClick={onContact}
          >
            <PhoneOutlinedIcon className={iconSizeClassName} />
          </IconButton>
        </ToolTip>
      )}
      {showClear && (
        <ToolTip title={`Clear ${providerText}`}>
          <IconButton
            className={buttonClassName}
            aria-label={`Clear ${providerText}`}
            onClick={onClear}
          >
            <CloseIcon className={iconSizeClassName} />
          </IconButton>
        </ToolTip>
      )}
    </div>
  );
}

type SelectedProviderButtonProps = {
  value?: ProviderEntryDTO | null;
  text: string;
  classes?: Partial<Record<Parts, string>>;
  onEdit: MouseEventHandler<HTMLButtonElement>;
  onClear: MouseEventHandler<HTMLButtonElement>;
  onContact: MouseEventHandler<HTMLButtonElement>;
  showContactButton: boolean;
  disabled?: boolean;
  variant?: 'button' | 'section';
};

function SelectedProviderButton({
  value,
  text,
  classes,
  onEdit,
  onClear,
  onContact,
  showContactButton,
  disabled,
  variant = 'button',
}: SelectedProviderButtonProps) {
  const empty = isNil(value);
  const { type, organizationName } = value ?? {};
  const isOrganization = type === 'NPI-2 Organization';
  const providerName = isOrganization
    ? organizationName
    : getDisplayName(value?.formattedNames, `${value?.firstName ?? ''} ${value?.lastName ?? ''}`);
  const emptyText = variant === 'section' ? 'Not Set' : `Add ${text}`;

  return (
    <div className={clsx('w-full flex items-center gap-2', {
      'flex-wrap justify-end': variant === 'section',
    }, classes?.root)}
    >
      <Button
        variant="outlined"
        color="primary"
        className={clsx('h-8 !min-w-[initial] !max-w-[initial]', {
          '!border-blue-500 !pl-3 !border-dashed w-full': empty && !disabled,
          '!border-gray-300 !text-gray-400 !pl-3 !border-dashed w-full': empty && disabled,
          '!pr-3 !text-black !max-w-[calc(100%-32px)]': !empty,
        }, classes?.button)}
        onClick={onEdit}
        disabled={disabled}
      >
        {empty && (<AddIcon className="!w-4 !h-4 !text-inherit" />)}
        {empty && emptyText}
        {!empty && (
          <p className={clsx('w-full flex items-center gap-2 !font-normal !min-w-[240px]', classes?.valueContainer)}>
            <span className={clsx('max-w-full truncate uppercase', classes?.name)}>
              {providerName}
            </span>
            <NPIInfo {...(value?.npi ?? {})} state={value?.state} />
          </p>
        )}
        {!empty && (<EditIcon className="ml-1 !w-5 !h-5 !text-inherit" />)}
      </Button>
      {!empty && (
        <ToolTip title={`Clear ${text}`}>
          <IconButton
            className="!p-0"
            onClick={onClear}
          >
            <CloseIcon className="!w-4 !h-4" />
          </IconButton>
        </ToolTip>
      )}
      {!empty && showContactButton && (
        <Button
          variant="text"
          color="primary"
          className="!min-w-[initial] !px-1 !h-8"
          onClick={onContact}
        >
          Contact
        </Button>
      )}
    </div>
  );
}

type CompactProviderCardProps = {
  value?: ProviderEntryDTO | null;
  text: string;
  classes?: Partial<Record<Parts, string>>;
  onEdit: MouseEventHandler<HTMLButtonElement>;
  onClear: MouseEventHandler<HTMLButtonElement>;
  onContact: MouseEventHandler<HTMLButtonElement>;
  showContactButton: boolean;
  dateSeen?: DateSeenProps;
  bordered?: boolean;
  dialogOpen?: boolean;
};

function CompactProviderCard({
  value,
  text,
  classes,
  onEdit,
  onClear,
  onContact,
  showContactButton,
  dateSeen,
  bordered = false,
  dialogOpen = false,
}: CompactProviderCardProps) {
  const editableRef = useRef<EditableInstance>(null!);
  const { type, organizationName, formattedNames } = value ?? {};
  const providerName = type === 'NPI-2 Organization'
    ? organizationName
    : getDisplayName(value?.formattedNames, '--');

  useEffect(() => {
    if (!dialogOpen) {
      editableRef.current?.setIsEdit(false);
    }
  }, [dialogOpen]);

  const providerView = (
    <Name
      {...(formattedNames ?? {})}
      name={providerName}
      className={clsx(
        '!w-auto min-w-0 flex-1 font-semibold text-left',
        classes?.name,
      )}
    />
  );

  return bordered && value ? (
    <ProviderCard
      provider={value}
      classes={{ root: classes?.root, name: classes?.name }}
      actions={(
        <ProviderActions
          text={text}
          onEdit={onEdit}
          onClear={onClear}
          onContact={onContact}
          showContactButton={showContactButton}
          compact
        />
      )}
    >
      <CompactDateEditor text={text} dateSeen={dateSeen} className={classes?.date} alignment="left" />
    </ProviderCard>
  ) : (
    <div className={clsx('flex w-full flex-col gap-1', classes?.root)}>
      <div className="flex w-full items-center justify-end gap-1">
        <Editable
          instanceRef={editableRef}
          idleView={providerView}
          onShowControl={(event) => {
            onEdit(event as MouseEvent<HTMLButtonElement>);
          }}
          tooltip={`Click to change ${text.toLowerCase()}`}
          classes={{ idleRoot: 'max-w-full' }}
        >
          {providerView}
        </Editable>
        <ToolTip title={`Clear ${text.toLowerCase()}`}>
          <IconButton
            className="!p-1 relative -top-0.5"
            aria-label={`Clear ${text.toLowerCase()}`}
            onClick={onClear}
          >
            <CloseIcon className="!h-5 !w-5 !text-black" />
          </IconButton>
        </ToolTip>
      </div>
      <CompactDateEditor
        text={text}
        dateSeen={dateSeen}
        className={classes?.date}
        alignment="right"
      />
    </div>
  );
}

function ProviderLoading({
  text,
  variant,
  classes,
}: Pick<Props, 'text' | 'variant' | 'classes'>) {
  const bordered = variant === 'compactBorder';

  return (
    <div
      className={clsx(
        bordered
          ? 'flex w-full flex-col gap-2 rounded border border-gray-200 px-3 py-2'
          : 'flex w-full flex-col gap-1',
        classes?.root,
      )}
      aria-busy="true"
      aria-label={`Loading ${text?.toLowerCase() ?? 'referring provider'}`}
    >
      <div className="flex w-full items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-gray-200" />
        </div>
        <div className="flex shrink-0 gap-1">
          <div className="h-8 w-8 animate-pulse rounded bg-gray-200" />
          <div className="h-8 w-8 animate-pulse rounded bg-gray-200" />
        </div>
      </div>
      {bordered && <div className="h-7 w-2/3 animate-pulse rounded bg-gray-200" />}
    </div>
  );
}

function ProviderButton({
  value,
  onClear,
  onOpenContact,
  onOpenDialog,
  classes,
  text = 'Referring Provider',
  variant = 'button',
  showContactButton,
  dateSeen,
  dialogOpen = false,
  disabled,
}: ProviderButtonProps) {
  const showCompact = !isNil(value) && (variant === 'compact' || variant === 'compactBorder');

  return showCompact ? (
    <CompactProviderCard
      value={value}
      text={text}
      classes={classes}
      onEdit={onOpenDialog}
      onClear={onClear}
      onContact={onOpenContact}
      showContactButton={showContactButton}
      dateSeen={dateSeen}
      dialogOpen={variant === 'compact' && dialogOpen}
      bordered={variant === 'compactBorder'}
    />
  ) : (
    <SelectedProviderButton
      value={value}
      text={text}
      classes={classes}
      onEdit={onOpenDialog}
      onClear={onClear}
      onContact={onOpenContact}
      showContactButton={showContactButton}
      disabled={disabled}
      variant={variant === 'section' ? 'section' : 'button'}
    />
  );
}

export default function ReferringProvider({
  onChange,
  dialogHeight,
  value,
  loading = false,
  classes,
  hideBackdrop = true,
  text = 'Referring Provider',
  action,
  suggestions = [],
  variant = 'button',
  dateSeen,
  showContactButton = false,
  disabled = false,
}: Props) {
  const contactTarget = !isNil(value)
    ? getProviderEntryConfigTarget(value)
    : null;

  const {
    mode,
    setMode,
    open,
    contactOpen,
    openDialog,
    closeDialog,
    openContact,
    closeContact,
  } = useReferringProviderState();

  return (
    <>
      {loading && (
        <ProviderLoading
          text={text}
          variant={variant}
          classes={classes}
        />
      )}
      {!loading && (
        <>
          <ProviderButton
            value={value}
            onOpenDialog={() => { openDialog(0); }}
            onClear={() => { onChange(null); }}
            onOpenContact={() => { openContact(); }}
            classes={classes}
            text={text}
            variant={variant}
            showContactButton={showContactButton}
            dateSeen={dateSeen}
            dialogOpen={open}
            disabled={disabled}
          />
          {action}
        </>
      )}
      {contactOpen && !isNil(contactTarget) && (
        <ProviderContactConfigDialog
          target={contactTarget}
          onClose={closeContact}
        />
      )}
      <ProviderSearchDialog
        open={open}
        mode={mode}
        text={text}
        value={value}
        suggestions={suggestions}
        onChange={onChange}
        onClose={closeDialog}
        onModeChange={setMode}
        hideBackdrop={hideBackdrop}
        dialogHeight={dialogHeight}
      />
    </>
  );
}
